import React, { Component } from 'react';
import { Card, Button, Input, List, Avatar, Spin, message, Modal, Tag, Alert, Row, Col, Space, Tooltip, Divider, Typography } from 'antd';
import { 
  RobotOutlined, 
  SendOutlined, 
  ThunderboltOutlined, 
  MessageOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ClearOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { aiConfigApi } from '../../../../lib/aiConfigApi';
import { AccountMetaData } from '../../../../config/settings';

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;
const AI_PROVIDER_CONNECTION_ERROR = 'AI_PROVIDER_CONNECTION_ERROR';
const AI_PROVIDER_CONNECTION_MESSAGE = 'Cấu hình kết nối API đang gặp sự cố, vui lòng kiểm tra.';

export default class AIArchitectTab extends Component {
  constructor(props) {
    super(props);
    
    // Retrieve or generate persistent session ID
    const agentId = props.config?.id || 0;
    const storageKey = `nexa_ai_session_${agentId}`;
    let sessionId = localStorage.getItem(storageKey);
    
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(storageKey, sessionId);
    }

    this.state = {
      sessionId,
      messages: [],
      inputValue: '',
      loading: false,
      streaming: false,
      quickModeVisible: false,
      quickDescription: '',
      pollingStatus: null,
      pollingInterval: null,
      suggestedConfig: null,
      confirmLoading: false
    };
    this.chatEndRef = React.createRef();
  }

  getStorageKey = () => {
    return `nexa_ai_session_${this.props.config?.id || 0}`;
  };

  generateNewSession = () => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(this.getStorageKey(), newSessionId);
    return newSessionId;
  };

  componentDidMount() {
    this.loadChatHistory();
  }

  componentDidUpdate() {
    this.scrollToBottom();
  }

  componentWillUnmount() {
    if (this.state.pollingInterval) {
      clearInterval(this.state.pollingInterval);
    }
  }

  handleClearChat = () => {
    Modal.confirm({
      title: 'Xóa lịch sử trò chuyện?',
      content: 'Toàn bộ nội dung trao đổi với AI Architect trong phiên này sẽ bị xóa.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          // Clear backend session
          await aiConfigApi.clearArchitectSession(this.state.sessionId);
          
          // Generate new session and update local state
          const newSessionId = this.generateNewSession();
          this.setState({
            messages: [],
            sessionId: newSessionId,
            suggestedConfig: null
          });
          message.success('Đã xóa lịch sử trò chuyện');
        } catch (error) {
          // Fallback: refresh session locally
          const newSessionId = this.generateNewSession();
          this.setState({
            messages: [],
            sessionId: newSessionId,
            suggestedConfig: null
          });
          message.info('Đã làm mới phiên trò chuyện');
        }
      }
    });
  };

  scrollToBottom = () => {
    if (this.chatEndRef.current) {
      this.chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  showArchitectError = (error) => {
    const errorCode = error?.code || error?.errorCode;
    const errorText = error?.message || String(error || '');
    const isConnectionError = errorCode === AI_PROVIDER_CONNECTION_ERROR
      || /openrouter|api key|network|fetch|failed to fetch|http error/i.test(errorText);

    const content = isConnectionError
      ? AI_PROVIDER_CONNECTION_MESSAGE
      : 'Không thể xử lý yêu cầu tạo AI Chatbot. Vui lòng thử lại.';

    message.error({
      key: 'ai-architect-api-error',
      content,
      duration: 6
    });
    return content;
  };

  consumeSSEStream = async (response, onData) => {
    if (!response.ok) {
      const error = new Error(`HTTP error ${response.status}`);
      error.code = AI_PROVIDER_CONNECTION_ERROR;
      throw error;
    }
    if (!response.body) {
      const error = new Error('Streaming response body is unavailable');
      error.code = AI_PROVIDER_CONNECTION_ERROR;
      throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const processEvent = async (eventText) => {
      const dataLine = eventText
        .split('\n')
        .find((line) => line.startsWith('data: '));
      if (!dataLine) return;

      try {
        const data = JSON.parse(dataLine.substring(6));
        await onData(data);
      } catch (error) {
        if (error?.code || error?.errorCode) throw error;
        console.error('Error parsing SSE data', error);
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

      const events = buffer.split('\n\n');
      buffer = events.pop() || '';
      for (const eventText of events) {
        await processEvent(eventText);
      }

      if (done) break;
    }

    if (buffer.trim()) {
      await processEvent(buffer);
    }
  };

  loadChatHistory = async () => {
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    try {
      const agentId = this.props.config?.id || 0;
      const res = await aiConfigApi.getArchitectHistory(agentId, this.state.sessionId);
      if (res.data && res.data.messages) {
        this.setState({ 
          messages: res.data.messages.map(m => ({
            role: m.role,
            content: m.content,
            id: m.id || Math.random()
          }))
        });
      }
    } catch (error) {
      console.warn('Could not load chat history', error);
    }
  };

  handleSendMessage = async () => {
    const { inputValue, messages, sessionId, streaming } = this.state;
    if (!inputValue.trim() || streaming) return;

    const userMessage = { role: 'user', content: inputValue, id: Date.now() };
    const newMessages = [...messages, userMessage];
    const assistantMessage = { role: 'assistant', content: '', id: Date.now() + 1 };
    
    this.setState({
      messages: [...newMessages, assistantMessage],
      inputValue: '',
      streaming: true
    });

    try {
      const companyId = this.props.companySelected?.company_id;
      const url = aiConfigApi.getStreamingUrl('architect/chat/stream');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': AccountMetaData.getToken(),
          'Company': companyId || AccountMetaData.getCompanyId()
        },
        body: JSON.stringify({
          sessionId,
          message: inputValue,
          context: {
            agentId: this.props.config?.id || 0,
            currentAgentName: this.props.config?.agentName,
            currentConfig: this.props.config
          }
        })
      });

      await this.consumeSSEStream(response, (data) => {
        if (data.error) {
          const error = new Error(data.error);
          error.code = data.errorCode || AI_PROVIDER_CONNECTION_ERROR;
          throw error;
        }

        if (data.token) {
          assistantMessage.content += data.token;
          this.updateLastMessage(assistantMessage);
        }

        if (data.done) {
          const toolCall = data.tool_call || data.toolCall;
          if (toolCall) {
            this.setState({ suggestedConfig: toolCall });
          }
        }
      });
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = this.showArchitectError(error);
      this.updateLastMessage({
        role: 'assistant',
        content: errorMessage,
        id: Date.now() + 1,
        isError: true
      });
    } finally {
      this.setState({ streaming: false });
    }
  };

  updateLastMessage = (updatedMsg) => {
    this.setState(prevState => {
      const newMessages = [...prevState.messages];
      newMessages[newMessages.length - 1] = { ...updatedMsg };
      return { messages: newMessages };
    });
  };

  handleQuickMode = async () => {
    const { quickDescription } = this.state;
    if (!quickDescription.trim()) {
      return message.warning('Vui lòng nhập mô tả cho Agent');
    }

    this.setState({ 
      inputValue: quickDescription, 
      quickModeVisible: false,
      quickDescription: '' 
    }, () => {
      this.handleSendMessage();
    });
  };


  handleConfirmCreation = async () => {
    const { suggestedConfig, sessionId } = this.state;
    if (!suggestedConfig) return;

    this.setState({ confirmLoading: true });

    try {
      const companyId = this.props.companySelected?.company_id;
      const url = aiConfigApi.getStreamingUrl('architect/confirm/stream');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': AccountMetaData.getToken(),
          'Company': companyId || AccountMetaData.getCompanyId()
        },
        body: JSON.stringify({
          sessionId,
          agentId: this.props.config?.id || 0,
          companyId: companyId || AccountMetaData.getCompanyId(),
          args: suggestedConfig.arguments
        })
      });

      await this.consumeSSEStream(response, (data) => {
        if (data.status === 'completed') {
          message.success('Đã cấu hình Agent thành công!');
          this.setState({ suggestedConfig: null });
          if (this.props.onRefresh) this.props.onRefresh();
        } else if (data.status === 'error' || data.error) {
          const error = new Error(data.error || 'AI Architect error');
          error.code = data.errorCode || AI_PROVIDER_CONNECTION_ERROR;
          throw error;
        }
      });
    } catch (error) {
      console.error('Confirm error:', error);
      this.showArchitectError(error);
    } finally {
      this.setState({ confirmLoading: false });
    }
  };

  renderChatMessage = (msg) => {
    const isAi = msg.role === 'assistant';
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: isAi ? 'flex-start' : 'flex-end',
        marginBottom: 16,
        padding: '0 4px'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '88%',
          alignItems: 'flex-start'
        }}>
          {isAi && (
            <div style={{ 
              width: 30, 
              height: 30, 
              borderRadius: '6px', 
              backgroundColor: '#f0f7ff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginRight: 8,
              flexShrink: 0,
              border: '1px solid #bae7ff'
            }}>
              <Avatar 
                src="/ai.png" 
                size={24} 
                shape="square"
                icon={<RobotOutlined style={{ color: '#1890ff' }} />}
                style={{ backgroundColor: 'transparent' }}
              />
            </div>
          )}
          
          <div style={{ 
            backgroundColor: isAi ? '#f5f7f9' : '#1890ff',
            color: isAi ? '#1a1a1a' : '#fff',
            padding: '10px 16px',
            borderRadius: isAi ? '2px 10px 10px 10px' : '10px 2px 10px 10px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            border: isAi ? '1px solid #e8ebee' : 'none',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {isAi ? (
              <ReactMarkdown 
                className="markdown-content"
                components={{
                  p: ({node, ...props}) => <p style={{ marginBottom: 6 }} {...props} />,
                  ul: ({node, ...props}) => <ul style={{ paddingLeft: 18, marginBottom: 6 }} {...props} />,
                  li: ({node, ...props}) => <li style={{ marginBottom: 2 }} {...props} />,
                  strong: ({node, ...props}) => <strong style={{ fontWeight: 600, color: '#1890ff' }} {...props} />,
                }}
              >
                {msg.content}
              </ReactMarkdown>
            ) : (
              msg.content
            )}
          </div>
          
          {!isAi && (
            <div style={{ 
              width: 30, 
              height: 30, 
              borderRadius: '50%', 
              backgroundColor: '#1890ff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginLeft: 8,
              flexShrink: 0,
              boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
            }}>
              <MessageOutlined style={{ fontSize: 12, color: '#fff' }} />
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { 
      messages, inputValue, streaming, 
      suggestedConfig, confirmLoading
    } = this.state;

    const isNewSession = messages.filter(m => m.role !== 'system').length === 0;

    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#fff' }}>
        {/* MESSAGES LIST AREA - SCROLLABLE */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '20px 12px', 
          backgroundColor: '#fff',
          borderRadius: '10px 10px 0 0',
          scrollbarWidth: 'thin'
        }}>
          {isNewSession && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '40px 0'
            }}>
              <div style={{ 
                width: 50, height: 50, borderRadius: '12px', 
                backgroundColor: '#f0f7ff', display: 'flex', 
                alignItems: 'center', justifyContent: 'center',
                marginBottom: 16,
                border: '1px solid #bae7ff'
              }}>
                <Avatar src="/ai.png" size={32} />
              </div>
              <Title level={4} style={{ marginBottom: 8, fontWeight: 700 }}>Nexa Architect AI</Title>
              <Paragraph style={{ color: '#8c8c8c', textAlign: 'center', maxWidth: 350, fontSize: 13 }}>
                Hãy mô tả mục tiêu kinh doanh của bạn. Tôi sẽ giúp bạn thiết kế AI Agent tối ưu nhất.
              </Paragraph>
              
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <Button size="small" shape="round" onClick={() => this.setState({ inputValue: 'Thiết kế agent bán mỹ phẩm phong thủy' })}>Mỹ Phẩm</Button>
                <Button size="small" shape="round" onClick={() => this.setState({ inputValue: 'Agent tư vấn bất động sản' })}>Bất Động Sản</Button>
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <div key={m.id || idx}>
              {this.renderChatMessage(m)}
            </div>
          ))}
          
          {streaming && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, color: '#1890ff', fontSize: 12 }}>
              <LoadingOutlined /> <span>AI đang soạn thảo...</span>
            </div>
          )}

          {suggestedConfig && (
            <div style={{ 
              margin: '16px 4px', 
              padding: '16px', 
              border: '1px solid #bae7ff', 
              borderRadius: '10px', 
              backgroundColor: '#f0faff',
              boxShadow: '0 2px 8px rgba(24, 144, 255, 0.05)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2, color: '#003a8c' }}>
                    Cấu hình đề xuất
                  </div>
                  <div style={{ fontSize: 12, color: '#595959' }}>
                    Click xác nhận để áp dụng bản thiết kế mới.
                  </div>
                </div>
                <Button 
                  type="primary" 
                  size="middle"
                  icon={<ThunderboltOutlined />}
                  onClick={this.handleConfirmCreation}
                  loading={confirmLoading}
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          )}
          <div ref={this.chatEndRef} />
        </div>

        {/* INPUT AREA - COMPACT */}
        <div style={{ 
          padding: '12px 16px', 
          backgroundColor: '#fff',
          borderTop: '1px solid #f0f0f0'
        }}>
          <div style={{ 
            backgroundColor: '#f5f7f9', 
            borderRadius: '12px', 
            padding: '4px 12px',
            border: '1px solid #e8ebee',
            display: 'flex',
            alignItems: 'center'
          }}>
            <TextArea
              value={inputValue}
              onChange={(e) => this.setState({ inputValue: e.target.value })}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  this.handleSendMessage();
                }
              }}
              placeholder="Nhập yêu cầu tại đây..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              style={{ 
                flex: 1, 
                border: 'none', 
                boxShadow: 'none', 
                padding: '8px 0',
                fontSize: '14px',
                backgroundColor: 'transparent'
              }}
            />
            <Button 
              icon={<ClearOutlined />} 
              onClick={this.handleClearChat}
              title="Xóa lịch sử"
              style={{ 
                height: '34px', 
                width: '34px', 
                borderRadius: '8px',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                color: '#bfbfbf',
                marginRight: 4
              }}
              disabled={streaming || messages.length === 0}
            />
            <Button 
              type="primary" 
              icon={<SendOutlined />} 
              onClick={this.handleSendMessage}
              style={{ 
                height: '34px', 
                width: '34px', 
                borderRadius: '8px',
                backgroundColor: '#722ed1',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              disabled={!inputValue.trim() || streaming}
            />
          </div>
          <div style={{ 
            marginTop: 8, 
            fontSize: 9, 
            color: '#bfbfbf', 
            textAlign: 'center', 
            letterSpacing: '0.05em'
          }}>
            NỘI DUNG ĐƯỢC TẠO BỞI AI. VUI LÒNG KIỂM TRA LẠI ĐỘ CHÍNH XÁC.
          </div>
        </div>
      </div>
    );
  }
}
